import debounce from "lodash.debounce";
import { getDefaultStore } from "jotai";
import { captureMessage } from "@sentry/nextjs";
import { Folder, folderToClient, Note, noteToClient } from "../../../shared/types";
import { IdeaflowUser } from "../../auth/useAuth";
import {
  ClientToSWMessageType,
  SWToClientMessage,
  SWToClientMessageType,
} from "../../service-worker/message/swMessage";
import { isAuthEnabled, isLocal, modelVersion } from "../../utils/environment";
import logger from "../../utils/logger";
import { sendMessageToSW, sendMessageToSWWhenReady } from "../sync/sendMessageToSW";
import {
  deltaCheckpoint,
  hasDirtyAtom,
  isLoadedAtom,
  isOnlineAtom,
  isPersistingAtom,
  SyncStateAtom,
  syncStateAtom,
} from "../atoms";
import { ApiClient } from "../../api/client";
import { CacheKeys, readCache, upsertToCache, vacuumCache } from "../../service-worker/swCache";
import { getOrCreateDeviceId } from "../../utils/deviceId";
import { NoteStore } from "./NoteStore";
import { FolderStore } from "./FolderStore";
import { UserStore } from "./UserStore";
import { createUserAndThoughtstream, migrateNote } from "./utils";

const setCheckpoint = async (n: number) => {
  console.log("[debug] setCheckpoint", n);
  await upsertToCache({ metadata: { checkpoint: n } });
  getDefaultStore().set(deltaCheckpoint, n);
};

const upsertSyncState = (data: Partial<SyncStateAtom>): void => {
  getDefaultStore().set(syncStateAtom, {
    ...getDefaultStore().get(syncStateAtom),
    ...data,
  });
};

export class LocalStore {
  public refreshEditor: ReturnType<typeof debounce>;
  public markDataAsLoaded: () => void;

  public sendFreshTokenToSW: () => Promise<void>;

  private ackIdCallbacks: Map<string, (value: any) => void>;
  private noteStore: NoteStore;
  private folderStore: FolderStore;
  private userStore: UserStore;
  constructor(noteStore: NoteStore, folderStore: FolderStore, userStore: UserStore) {
    this.noteStore = noteStore;
    this.folderStore = folderStore;
    this.userStore = userStore;
    this.refreshEditor = debounce(() => {});
    this.markDataAsLoaded = () => {};

    this.sendFreshTokenToSW = () => Promise.resolve();

    this.ackIdCallbacks = new Map();
  }

  async loadAllData(modelVersion: string, localUser: IdeaflowUser): Promise<void> {
    //Sometimes loadAllData is called again because of Auth, make sure we do not do two
    //parallel downloads.
    if (getDefaultStore().get(syncStateAtom).isDownloading || getDefaultStore().get(isLoadedAtom)) {
      return;
    }
    console.log("[debug] Trying to load from cache");
    const wasLoadedFromCache = await this.loadFromCache();
    console.log("[debug]", { wasLoadedFromCache });
    if (!wasLoadedFromCache && isAuthEnabled) {
      console.log("[debug] Loading from server");
      await this.loadFromServer(localUser);
      console.log("[debug] Finished loading from server");
    }
    console.log("[debug] Marking app as loaded");
    this.markDataAsLoaded();
    getDefaultStore().set(isLoadedAtom, true);
  }

  async loadFromCache(): Promise<boolean> {
    console.log("[debug] loadFromCache");
    const cachedData = await readCache();

    const hasCachedData = !!(cachedData[CacheKeys.notes]?.length || cachedData[CacheKeys.folders]?.length);
    let cachedCheckpoint = cachedData?.metadata?.checkpoint;

    if (!cachedCheckpoint) {
      console.log("[debug] cache status", {
        hasCachedData,
        cachedCheckpoint,
      });
    }

    if (hasCachedData && !cachedCheckpoint) {
      if (isAuthEnabled) {
        //If auth is enabled, and we have cached data but no check
        logger.error("Missing checkpoint but auth is enabled and cache has data", true);
      }
      console.log("[debug] Setting missing cachedCheckpoint since we have data in cache.");
      cachedCheckpoint = new Date().getTime();
    }

    if (!cachedCheckpoint) {
      getDefaultStore().set(syncStateAtom, {
        ...getDefaultStore().get(syncStateAtom),
        lastFullySyncedAt: new Date().getTime(),
      });
      console.log("[debug] No cached checkpoint");
      return false;
    }

    this.folderStore.upsert(cachedData.folders ?? [], false);
    this.noteStore.upsert(cachedData.notes ?? [], false);
    this.folderStore.dirtyIds.import(cachedData[CacheKeys.foldersDirtySet] ?? []);
    this.noteStore.dirtyIds.import(cachedData[CacheKeys.notesDirtySet] ?? []);

    const provisionalNoteIds = this.noteStore
      .getAll(true)
      .filter((n) => n.position.type === "on-top-provisional")
      .map((n) => n.id);
    if (provisionalNoteIds.length) {
      await this.noteStore.dirtyIds.flag(provisionalNoteIds);
    }

    this.userStore.setFromCache({
      userSettings: cachedData.user?.userSettings || {},
      isDirty: cachedData.user?.isDirty || false,
    });
    console.log("[debug] Setting checkpoint from cache", cachedCheckpoint);
    //Todo: Load user settings from cache
    await setCheckpoint(cachedCheckpoint);
    getDefaultStore().set(syncStateAtom, {
      ...getDefaultStore().get(syncStateAtom),
      lastFullySyncedAt: cachedCheckpoint,
      appNoteCount: this.noteStore.getSize(),
      appFolderCount: this.folderStore.getSize(),
    });
    getDefaultStore().set(
      hasDirtyAtom,
      this.noteStore.dirtyIds.getAll().length + this.folderStore.dirtyIds.getAll().length > 0,
    );
    return true;
  }

  /**
   * Uploads all dirty notes, folders and user settings to the server
   * @param modelVersion
   */
  async sync(modelVersion: string) {
    try {
      if (
        getDefaultStore().get(isPersistingAtom) ||
        !getDefaultStore().get(isOnlineAtom) ||
        !getDefaultStore().get(isLoadedAtom)
      ) {
        console.log("[debug] Early return", {
          isPersisting: getDefaultStore().get(isPersistingAtom),
          isOnline: getDefaultStore().get(isOnlineAtom),
          isLoaded: getDefaultStore().get(isLoadedAtom),
        });
        return;
      }
      getDefaultStore().set(isPersistingAtom, true);

      //Persist user settings, pinned hashtags
      await this.userStore.persist();
      await this.refreshSyncState();

      const syncData = getDefaultStore().get(syncStateAtom);

      // If server has more updates after our checkpoint
      if (syncData.serverHasUpdates) {
        console.log("[debug] serverHasUpdates");
        const wereUpdatesApplied = await this.downloadUpdatesFromServer();
        if (wereUpdatesApplied) {
          console.log("[debug] server updates were applied");
          getDefaultStore().set(isPersistingAtom, false);
          return;
        }
        console.log("[debug] server updates were not applied");
      }

      // Server has no new updates after our checkpoint but we are still missing notes
      // Must mean we are missing notes in past
      if (syncData.serverNoteCount > this.noteStore.getSize()) {
        console.log("[debug] trying to download missing notes", {
          serverNoteCount: syncData.serverNoteCount,
          localNoteCount: this.noteStore.getSize(),
        });
        await this.downloadPastData();
        getDefaultStore().set(isPersistingAtom, false);
        console.log("[debug] downloaded missing notes", {
          serverNoteCount: syncData.serverNoteCount,
          localNoteCount: this.noteStore.getSize(),
        });
        return;
      }

      // Only upload if we have dirty notes locally and we have everything downloaded from server
      // because we calculate positions based on all notes So we need all notes in local.
      // Check if we don't have any updates from server OR they weren't applied
      // because all they updates were for the dirty notes
      // Todo: We should probably have logic to handle this in the backend.
      console.log("[debug] uploading notes to server", {
        serverNoteCount: syncData.serverNoteCount,
        localNoteCount: this.noteStore.getSize(),
        dirtyCount: this.noteStore.getDirtyNodeIds().length,
      });
      await this.uploadUpdatesToServer();

      await this.refreshSyncState();
    } catch (error) {
      logger.error("sync failed", { error });
    }
    getDefaultStore().set(isPersistingAtom, false);
  }

  async uploadUpdatesToServer(): Promise<void> {
    const dirtyNoteIds = this.noteStore.getDirtyNodeIds();
    const dirtyFolderIds = this.folderStore.getDirtyFolderIds();

    const shouldUpload = dirtyNoteIds.length > 0 || dirtyFolderIds.length > 0;

    if (!shouldUpload) {
      upsertSyncState({
        pendingUpdateCount: 0,
        lastFullySyncedAt: new Date().getTime(),
      });
      console.log("[debug] uploadUpdatesToServer", {
        shouldUpload,
      });
      return;
    }

    const results = await Promise.all([this.noteStore.persist(), this.folderStore.persist()]);
    const uploadsWereSuccess = results.every((result) => result);

    if (!uploadsWereSuccess) {
      upsertSyncState({
        pendingUpdateCount: dirtyNoteIds.length + dirtyFolderIds.length,
      });
    } else {
      upsertSyncState({
        pendingUpdateCount: this.noteStore.getDirtyNodeIds().length + this.folderStore.getDirtyFolderIds().length,
        lastFullySyncedAt: new Date().getTime(),
      });
    }

    console.log("[debug] uploadUpdatesToServer result", {
      shouldUpload,
      uploadsWereSuccess,
    });
  }

  async downloadUpdatesFromServer(): Promise<boolean> {
    const dirtyNoteIds = this.noteStore.getDirtyNodeIds();
    const dirtyFolderIds = this.folderStore.getDirtyFolderIds();
    const { notes, folders } = await ApiClient().notes.getUserDelta(getDefaultStore().get(deltaCheckpoint));
    //Sometimes user is typing and we push an update and the user has continued to type but we pull the
    //pushed update and the users local update is overwritten, so ignore any downloads for dirtyIds in the same call.
    const formattedNotes: Note[] = notes
      .map((note) => noteToClient(note))
      .map(migrateNote)
      .filter((note) => !dirtyNoteIds.includes(note.id) && note.updatedByDeviceId !== getOrCreateDeviceId());

    // console.log("using downloaded notes length", formattedNotes.length, formattedNotes);
    const formattedFolders: Folder[] = folders
      .map((folder) => folderToClient(folder))
      .filter((folder) => !dirtyFolderIds.includes(folder.id));
    const shouldApplyServerUpdatesToLocal = formattedNotes.length > 0 || formattedFolders.length > 0;
    if (shouldApplyServerUpdatesToLocal) {
      this.noteStore.upsert(formattedNotes, false);
      this.folderStore.upsert(formattedFolders, false);
      await upsertToCache({ notes: formattedNotes, folders: formattedFolders });
      const checkpoints = [getDefaultStore().get(deltaCheckpoint)];
      if (formattedNotes.length > 0) {
        let maxNoteDate = checkpoints[0];
        for (const note of formattedNotes) {
          if (note.serverUpdateTimestamp) {
            maxNoteDate = Math.max(maxNoteDate, note.serverUpdateTimestamp.getTime());
          }
        }
        checkpoints.push(maxNoteDate);
      }
      if (formattedFolders.length > 0) {
        let maxFolderDate = checkpoints[0];
        for (const folder of formattedFolders) {
          if (folder.serverUpdateTimestamp) {
            maxFolderDate = Math.max(maxFolderDate, folder.serverUpdateTimestamp.getTime());
          }
        }
        checkpoints.push(maxFolderDate);
      }
      console.log(checkpoints);
      await setCheckpoint(Math.max(...checkpoints) + 1);
      console.log("refreshing editor");
      this.refreshEditor();
    }

    upsertSyncState({
      serverHasUpdates: false,
    });

    return shouldApplyServerUpdatesToLocal;
  }

  async downloadPastData() {
    console.log("downloading past data");
    upsertSyncState({
      downloadingPastData: true,
    });
    try {
      const CHUNK_SIZE = 2000;
      let localNoteIds = this.noteStore.getNoteIds();
      if (localNoteIds.length === 0) {
        await this.loadFromCache();
        this.refreshEditor();
      }
      localNoteIds = this.noteStore.getNoteIds();
      const serverNoteIds = await ApiClient().notes.getNoteIds();
      const missingNoteIds = serverNoteIds.filter((id) => !localNoteIds.includes(id));
      const missingNoteIdsChunked = missingNoteIds.slice(0, CHUNK_SIZE);
      const notes = await ApiClient().notes.getNotesByNoteIds(missingNoteIdsChunked);
      const formattedNotes: Note[] = notes.map((note) => noteToClient(note)).map(migrateNote);
      this.noteStore.upsert(formattedNotes, false);
      console.log(`downloaded ${notes.length}/${missingNoteIds.length} past notes`);
      await upsertToCache({ notes: formattedNotes });
      captureMessage(`Was downloading past data`, {
        level: "warning",
        extra: {
          localNoteIdsLength: localNoteIds.length,
          missingNoteIdsLength: missingNoteIds.length,
          chunkSize: CHUNK_SIZE,
          missingNoteIdsChunkedLength: missingNoteIdsChunked.length,
        },
      });
    } catch (e) {
      logger.error(e, true);
    }
    upsertSyncState({
      downloadingPastData: false,
    });
  }

  /**
   * Initally load data from server and set's the last downloaded note's updated as checkpoint.
   * We have another method that polls the server using that checkpoint. It should probably be merged
   * with this method.
   *
   * Todo: Merge `loadFromServer` with `loadFromServerUsingTimestamp` after this refactor.
   */
  async loadFromServer(localUser: IdeaflowUser): Promise<void> {
    getDefaultStore().set(syncStateAtom, {
      ...getDefaultStore().get(syncStateAtom),
      isDownloading: true,
      startTime: new Date().getTime(),
    });
    const shouldLoadMore = await this.loadUserSettingsAndStatsFromServer(localUser);
    if (shouldLoadMore) {
      await this.loadNotesFromServer();
      await this.loadFoldersFromServer();
    }
    // During initial load, *all* user data is loaded into the delta cache. This
    // makes upserting data into the cache very slow. Running the vacuum moves
    // the deltas to the main cache, which makes upserting data into the cache
    // fast again. - Taylor
    console.log("Vacuuming cache");
    await vacuumCache();
    getDefaultStore().set(syncStateAtom, {
      ...getDefaultStore().get(syncStateAtom),
      isDownloading: false,
    });
  }

  async loadNotesFromServer(): Promise<void> {
    let appNoteCount = 0;
    let offset = 0;
    const limit = 2000;
    let hasMore = true;
    let maxDate = getDefaultStore().get(deltaCheckpoint);
    while (hasMore) {
      const notes = (await ApiClient().notes.getNotesByUserId(limit, offset))
        .map((note) => noteToClient(note))
        .map(migrateNote);
      if (notes.length > 0) {
        this.noteStore.upsert(notes, false);
        await upsertToCache({ notes: notes });
        for (const note of notes) {
          if (note.serverUpdateTimestamp) {
            maxDate = Math.max(maxDate, note.serverUpdateTimestamp.getTime());
          }
        }
        await setCheckpoint(maxDate);
      }
      appNoteCount += notes.length;
      offset += limit;
      hasMore = notes.length === limit;
      console.table({
        message: "loading notes",
        appNoteCount: appNoteCount,
        offset: offset,
        limit: limit,
        hasMore: hasMore,
      });
      getDefaultStore().set(syncStateAtom, {
        ...getDefaultStore().get(syncStateAtom),
        appNoteCount: appNoteCount,
      });
    }
  }

  async loadFoldersFromServer(): Promise<void> {
    const folders = (await ApiClient().notes.getFoldersByUserId()).map((folder) => folderToClient(folder));
    let maxDate = getDefaultStore().get(deltaCheckpoint);
    if (folders.length > 0) {
      this.folderStore.upsert(folders, false);
      await upsertToCache({ folders: folders });
      for (const folder of folders) {
        if (folder.serverUpdateTimestamp) {
          maxDate = Math.max(maxDate, folder.serverUpdateTimestamp.getTime());
        }
      }
      await setCheckpoint(maxDate);
    }
    getDefaultStore().set(syncStateAtom, {
      ...getDefaultStore().get(syncStateAtom),
      appFolderCount: folders.length,
    });
  }

  async loadUserSettingsAndStatsFromServer(localUser: IdeaflowUser): Promise<boolean> {
    const [userResponse, statResponse] = await Promise.all([
      ApiClient().notes.getUserSettings(),
      ApiClient().notes.getUserStats(null),
    ]);
    if (Object.keys(userResponse).length === 0) {
      const { folders, notes, userSettings } = await createUserAndThoughtstream(localUser);
      await this.userStore.setFromServer(userSettings);
      getDefaultStore().set(syncStateAtom, {
        ...getDefaultStore().get(syncStateAtom),
        serverNoteCount: 0,
        serverFolderCount: 0,
        serverHasUpdates: false,
      });
      this.noteStore.upsert(notes, true);
      this.folderStore.upsert(folders, true);
      return false;
    }
    await this.userStore.setFromServer(userResponse.user.settings);
    getDefaultStore().set(syncStateAtom, {
      ...getDefaultStore().get(syncStateAtom),
      serverNoteCount: statResponse.noteCount,
      serverFolderCount: statResponse.folderCount,
      serverHasUpdates: false,
    });
    return true;
  }

  async upsertUserSettings(userSettings: any) {
    await this.userStore.upsert(userSettings);
  }

  //Handles return sync data
  handleSyncData(e: MessageEvent<SWToClientMessage>): void {
    const msgType = e.data.type;
    logger.info(`handling ${msgType} message from sw`, {
      namespace: "sw-message",
    });

    if (msgType === SWToClientMessageType.ACKNOWLEDGE) {
      // TODO: clean this up
      // Implementation with callback map was carried over from old handleSwMessage code but we can do better
      const callback = this.ackIdCallbacks.get(e.data.ackId);
      if (!callback) {
        logger.warn("Nothing waiting for the ack message :(");
        return;
      }
      callback(e.data.ok);
      return;
    }

    if (msgType === SWToClientMessageType.ERROR) {
      logger.fatal("USER SAW BIG RED ERROR", { context: { errorMessage: e.data.message }, namespace: "sw-message" });
      if (isLocal) {
        throw new Error(`Service worker error: ${e.data.message}\nSee console for more details.`);
      }
      return;
    }

    // When a new client is loaded, an old service worker may initially be handling
    // messages. All the message types below must be handled by the latest service
    // worker, so we ignore them if the model version doesn't match.
    const swModelVersion: string | undefined = (e.data as any)?.modelVersion;
    if (swModelVersion !== modelVersion) {
      logger.warn(`Ignoring ${msgType} message, model version mismatch`, {
        context: {
          client: modelVersion,
          sw: swModelVersion,
        },
      });
    }
  }

  hasUnsyncedData(): boolean {
    const dirtyIds = this.noteStore.getDirtyNodeIds();
    return dirtyIds.length > 0;
  }

  async refreshSyncState() {
    if (!getDefaultStore().get(isLoadedAtom) || !getDefaultStore().get(isOnlineAtom)) return;
    const { noteCount, folderCount, serverHasUpdates } = await ApiClient().notes.getUserStats(
      getDefaultStore().get(deltaCheckpoint),
    );
    getDefaultStore().set(syncStateAtom, {
      ...getDefaultStore().get(syncStateAtom),
      serverNoteCount: Number(noteCount),
      serverFolderCount: Number(folderCount),
      appNoteCount: this.noteStore.getSize(),
      appFolderCount: this.folderStore.getSize(),
      cacheNoteCount: 0,
      cacheFolderCount: 0,
      serverHasUpdates,
    });
    // console.table({
    //   appNoteCount: this.noteStore.getSize(),
    //   appFolderCount: this.folderStore.getSize(),
    // });
  }

  enableDebug(namespaces: string): void {
    sendMessageToSWWhenReady({ type: ClientToSWMessageType.DEBUG, namespaces });
  }

  debugErrorTest(): void {
    sendMessageToSW({
      type: ClientToSWMessageType.ERROR_TEST,
      error: "Test error from SW. Ignore this.",
    });
  }

  debugReset(): void {
    sendMessageToSW({ type: ClientToSWMessageType.RESET });
  }
}
